import { Base } from "../../handlers/base";
/**
 * Base class for nested handlers that operate on a parent object and manage nested items within it.
 * This class provides methods to find, delete, and merge changes for nested items.
 * It is designed to be extended by specific nested handlers that implement the logic for
 * managing nested items on a parent data handler:
 * - {@link PromiseHandler}
 * - {@link ObservableHandler}.
 *
 * @category Nested Handlers
 *
 * @template ParentHandler - The parent handler type, which extends {@link AnyApiHandler}.
 * @template KeyPath - The key path type for the nested items, which is a path in the parent handler's data structure.
 * @template Validators - The type of validators to apply, which can be a set of validation methods or never if no validation is needed.
 * @template FilterType - The type of filter to apply when searching for nested items, defaulting to an object with an `id` property.
 * @template Parent - The type of the parent object, derived from the parent handler.
 * @template Item - The type of the nested item, which is inferred from the parent handler's data structure based on the provided key path.
 */
export class BaseNestedHandler extends Base {
    /**
     * Sometimes parent data is needed to validate child data.
     * The key returned here is used to store parent data on the child object,
     * when the child object is being validated.
     */
    getKeyToPutParentOn() {
        return "parentData";
    }
    /**
     * Find nestedItems that match the filters
     * @param parent Parent object to lookup nestedItems on
     * @param filters Filters to apply to the lookup
     * @returns Array of nestedItems that match the filters
     */
    find(parent, filters) {
        if (!parent) {
            return [];
        }
        return this.findItems(parent, filters);
    }
    /**
     * Find one child that matches the filters
     * @param parent Parent object to lookup nestedItems on
     * @param filters Filters to apply to the lookup
     * @returns child object or undefined
     */
    find_once(parent, filters) {
        if (!parent) {
            return undefined;
        }
        return this.findItems(parent, filters)[0];
    }
    /**
     * Delete nestedItems from parent
     * @param parent Parent object to delete nestedItems from
     * @param identifiers identifier(s) to delete
     * @returns Partial<ParentType> - with nestedItems that match identifiers removed
     */
    delete(parent, identifiers) {
        this.assertParentIsDefined(parent);
        return this.deleteItems(parent, identifiers);
    }
    /**
     * Merge merge changes to a child back into parent.
     * This is used to update the parent with changes from the child,
     * by simply providing the child object with the updated values.
     * @param parent Parent to merge child into
     * @param child Partial child object to merge into parent
     * @returns Partial<ParentType>
     */
    merge(parent, child) {
        this.assertParentIsDefined(parent);
        return this.mergeChanges(parent, child);
    }
    assertParentIsDefined(parent) {
        if (!parent) {
            throw new Error("Parent is undefined");
        }
    }
}
//# sourceMappingURL=base.js.map